import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import heroBg from '../assets/hero-bg.svg';
import './Home.css';

const topics = [
  {
    id: 'kubernetes',
    label: 'Kubernetes',
    path: '/kubernetes',
    icon: '⚙️',
    color: 'var(--primary)',
    tint: 'rgba(43, 136, 255, 0.14)',
    level: 'Foundation',
    dur: '6 weeks',
    desc: 'Container orchestration, pods, services, deployments and cluster management.',
    modules: ['Pods & Services', 'Deployments & Scaling', 'RBAC & Security', 'TLS & PKI', 'Cluster Upgrades', 'Helm Charts'],
  },
  {
    id: 'openshift',
    label: 'OpenShift',
    path: '/openshift',
    icon: '🔴',
    color: '#ee0000',
    tint: 'rgba(238, 0, 0, 0.12)',
    level: 'Advanced',
    dur: '5 weeks',
    desc: 'Enterprise Kubernetes platform by Red Hat with built-in CI/CD and security.',
    modules: ['Architecture', 'Routes & Networking', 'Storage', 'Workloads', 'Operators'],
  },
  {
    id: 'python',
    label: 'Python',
    path: '/python',
    icon: '🐍',
    color: 'var(--cyan)',
    tint: 'rgba(6, 182, 212, 0.14)',
    level: 'Intermediate',
    dur: '7 weeks',
    desc: 'From core language features to frameworks, data science and automation.',
    modules: ['Core Syntax', 'OOP & Inheritance', 'Async / Await', 'Decorators', 'Testing'],
  },
  {
    id: 'aws',
    label: 'AWS',
    path: '/aws',
    icon: '☁️',
    color: 'var(--warm)',
    tint: 'rgba(251, 146, 60, 0.14)',
    level: 'Intermediate',
    dur: '8 weeks',
    desc: 'Amazon Web Services — EC2, S3, Lambda, RDS and cloud architecture patterns.',
    modules: ['VPC & Networking', 'EC2 & Compute', 'IAM & Security', 'S3 & Storage', 'Lambda'],
  },
  {
    id: 'ai',
    label: 'AI / ML',
    path: '/ai',
    icon: '🤖',
    color: 'var(--purple)',
    tint: 'rgba(168, 85, 247, 0.14)',
    level: 'Advanced',
    dur: '10 weeks',
    desc: 'Machine learning, deep learning, LLMs, prompt engineering and MLOps.',
    modules: ['Neural Nets', 'LLMs', 'RAG', 'Vector DBs', 'Agentic AI'],
  },
  {
    id: 'system-design',
    label: 'System Design',
    path: '/system-design',
    icon: '🏗️',
    color: 'var(--teal)',
    tint: 'rgba(20, 184, 166, 0.14)',
    level: 'Intermediate',
    dur: '6 weeks',
    desc: 'Scalable architectures, distributed systems, databases and design patterns.',
    modules: ['Load Balancing', 'Caching', 'Databases', 'Message Queues', 'CAP Theorem'],
  },
];

const certs = ['CKA', 'CKAD', 'CKS', 'AWS SAA', 'AWS SAP', 'RHCSA', 'OpenShift Admin'];

const pillars = [
  { n: '01', t: 'Simple Explanation', d: 'Every concept first explained in plain English — no jargon wall.' },
  { n: '02', t: 'Real-World Analogy', d: 'A Pod is an apartment, a Cluster is a city, an ENI is a desk phone.' },
  { n: '03', t: 'Visual Diagram', d: 'Architecture diagrams and ASCII flows you can trace step by step.' },
  { n: '04', t: 'Architecture Deep Dive', d: 'Internals, data flow and failure scenarios, not just definitions.' },
  { n: '05', t: 'Interactive Simulation', d: 'Walk through TLS handshakes and cluster upgrades, not just read about them.' },
  { n: '06', t: 'Hands-on Quizzes', d: 'Scenario-style exam questions with full explanations on every answer.' },
  { n: '07', t: 'Troubleshooting', d: 'Symptoms → investigation → root cause → fix, with real commands.' },
  { n: '08', t: 'Interview Questions', d: 'Beginner to architect level, with model answers baked into every guide.' },
  { n: '09', t: 'Certification Prep', d: 'Targeted practice for CKA, CKAD, CKS, AWS and OpenShift exams.' },
];

const KB = {
  tls: {
    topic: 'TLS Certificates in Kubernetes',
    simple: 'TLS in Kubernetes is how every component (API server, kubelet, etcd) proves who it is and encrypts traffic — using a cluster-wide PKI instead of one shared secret.',
    analogy: "Think of the cluster CA as a passport office: every component gets its own passport (certificate) signed by the same authority, so any two components can trust each other on sight.",
    code: 'kind: Certificate\nspec:\n  commonName: kube-apiserver\n  usages: ["server auth"]\n  issuerRef:\n    name: kubernetes-ca',
    codeLang: 'cert.yaml',
    fix: 'kubectl get csr                       # check pending requests\nopenssl x509 -in cert.pem -noout -dates # check expiry',
  },
  upgrade: {
    topic: 'Safe Cluster Upgrades',
    simple: 'A safe upgrade moves one control-plane node at a time, then workers, always keeping quorum and draining pods before touching a node.',
    analogy: 'Like renovating a hotel one floor at a time while keeping the lobby and enough rooms open — guests (workloads) never notice the building is being upgraded.',
    code: 'kubectl drain node-1 --ignore-daemonsets\nkubeadm upgrade apply v1.29.0\nkubectl uncordon node-1',
    codeLang: 'upgrade-steps',
    fix: '# Gotcha: always upgrade kubelet/kubectl AFTER\n# kubeadm on that node, never before.',
  },
  eni: {
    topic: 'ENI vs ENA in AWS',
    simple: 'An ENI is the virtual network card itself (IP, MAC, security groups). ENA is the high-performance driver that makes that card faster — they solve different problems.',
    analogy: 'ENI is the desk phone with its own extension. ENA is upgrading the phone line from copper to fiber — same phone, much faster line.',
    code: '# ENI: identity & flexibility\naws ec2 create-network-interface\n\n# ENA: throughput & latency\nethtool -i eth0 | grep driver   # should show "ena"',
    codeLang: 'eni-vs-ena',
    fix: '# Detach + reattach an ENI to move an IP/MAC\n# to a new instance without touching DNS.',
  },
  pending: {
    topic: 'Why is my Pod Pending?',
    simple: 'Pending means the scheduler accepted the Pod but no node can run it yet — usually insufficient resources or an unbound volume.',
    analogy: 'Your flight is booked (scheduled) but there is no free gate (node), so the plane just waits on the tarmac.',
    code: 'kubectl describe pod <name>\n# read Events at the bottom, e.g.\n#  "0/3 nodes available: insufficient cpu"\n#  "pod has unbound immediate PersistentVolumeClaims"',
    codeLang: 'investigate',
    fix: '# insufficient resources -> scale nodes / lower requests\n# unbound PVC            -> check a StorageClass exists\n# node taints            -> add a matching toleration',
  },
};

function answerFor(qRaw) {
  const q = (qRaw || '').toLowerCase();
  if (q.includes('tls') || q.includes('certificate') || q.includes('pki')) return { ...KB.tls };
  if (q.includes('upgrade') || q.includes('kubeadm')) return { ...KB.upgrade };
  if (q.includes('eni') || q.includes('ena') || q.includes('network interface')) return { ...KB.eni };
  if (q.includes('pending') || q.includes('crashloop') || q.includes('not running')) return { ...KB.pending };
  return {
    topic: qRaw,
    simple: 'Great question. At its core this is about one job and how the platform does it reliably so you do not have to babysit it.',
    analogy: 'Picture a well-run kitchen: clear roles, recipes (manifests) on the wall, and a manager who replaces anyone who burns out — that is the control loop at work.',
    code: '# In the full lesson this renders as an\n# interactive diagram you can trace\n# step by step from request to result.',
    codeLang: 'concept-map',
    fix: '# Every braincloud topic ends with the exact\n# commands to verify it yourself.',
  };
}

const suggestions = [
  'Explain TLS handshake in Kubernetes',
  'How does a safe cluster upgrade work?',
  "What's the difference between ENI and ENA?",
];

function loadProgress() {
  try {
    const raw = localStorage.getItem('braincloud-progress');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function Home() {
  const [progress, setProgress] = useState(loadProgress);
  const [messages, setMessages] = useState([
    {
      role: 'ai',
      topic: 'Welcome — try a question below',
      simple: 'I break every concept into a plain-English idea first, then go as deep as you want.',
      analogy: 'Think of me as a patient senior engineer who never makes you feel dumb for asking.',
      code: '# Tap a suggestion below, or type your own:\n#   "Explain TLS handshake"\n#   "Why is my Pod Pending?"',
      codeLang: 'getting-started',
      fix: 'kubectl get pods   # we will walk through real output together',
    },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('braincloud-progress', JSON.stringify(progress));
  }, [progress]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking]);

  const toggleModule = (topicId, idx) => {
    setProgress((p) => {
      const cur = p[topicId] || [];
      const next = cur.includes(idx) ? cur.filter((i) => i !== idx) : [...cur, idx];
      return { ...p, [topicId]: next };
    });
  };

  const send = (text) => {
    const q = (text != null ? text : input).trim();
    if (!q || thinking) return;
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      setMessages((m) => [...m, { role: 'ai', ...answerFor(q) }]);
      setThinking(false);
    }, 850);
  };

  const pipelineSteps = topics.map((t) => ({ key: t.id, label: t.label, sub: t.desc.split('.')[0], color: t.color }));

  return (
    <div className="home">
      {/* Background pattern */}
      <div className="bg-pattern" style={{ backgroundImage: `url(${heroBg})` }} />

      {/* Blobs */}
      <div className="bg-blob blob-1" />
      <div className="bg-blob blob-2" />

      {/* Hero */}
      <section className="home-hero" id="top">
        <div className="hero-grid">
          <div className="hero-left">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              Understanding, not memorization
            </div>
            <h1 className="hero-title">
              Master <span className="gradient-text">Kubernetes, OpenShift,<br />AWS, Python &amp; AI</span>
            </h1>
            <p className="hero-subtitle">
              Learn through visual explanations, real-world analogies, architecture diagrams, simulators,
              quizzes and an AI tutor — built so concepts actually stick.
            </p>
            <div className="hero-cta-row">
              <a href="#paths" className="btn-primary">Start Learning</a>
              <a href="#tutor" className="btn-secondary">Ask the AI Tutor</a>
            </div>
            <div className="hero-stats">
              <div className="hero-stat"><span className="hero-stat-num">6</span><span className="hero-stat-label">learning tracks</span></div>
              <div className="hero-stat"><span className="hero-stat-num">100+</span><span className="hero-stat-label">visual lessons</span></div>
              <div className="hero-stat"><span className="hero-stat-num">20+</span><span className="hero-stat-label">simulators &amp; quizzes</span></div>
              <div className="hero-stat"><span className="hero-stat-num">7</span><span className="hero-stat-label">cert tracks</span></div>
            </div>
          </div>

          {/* Animated pipeline panel */}
          <div className="pipeline-panel">
            <div className="pipeline-header">
              <div className="pipeline-live">
                <span className="pipeline-live-dot" />
                live: cloud-native stack
              </div>
              <div className="pipeline-dots">
                <span /><span /><span />
              </div>
            </div>
            <div className="pipeline-flow">
              {pipelineSteps.map((s, i) => (
                <div className="pipeline-step-wrap" key={s.key}>
                  <div className="pipeline-step" style={{ '--c': s.color, animationDelay: `${i * 0.25}s` }}>
                    <span className="pipeline-step-icon" style={{ background: s.color + '22', color: s.color }}>
                      {s.label.slice(0, 3).toUpperCase()}
                    </span>
                    <div>
                      <div className="pipeline-step-label">{s.label}</div>
                      <div className="pipeline-step-sub">{s.sub}</div>
                    </div>
                  </div>
                  {i < pipelineSteps.length - 1 && (
                    <div className="pipeline-connector">
                      <span className="pipeline-packet" style={{ background: s.color, boxShadow: `0 0 10px ${s.color}`, animationDelay: `${i * 0.25}s` }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cert strip */}
      <section className="cert-strip">
        <div className="cert-strip-inner">
          <span className="cert-strip-label">Prep for</span>
          <div className="cert-strip-list">
            {certs.map((c) => (
              <span className="cert-pill" key={c}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Learning paths */}
      <section className="paths-section" id="paths">
        <div className="section-head">
          <div className="section-eyebrow" style={{ color: 'var(--cyan)' }}>// learning paths</div>
          <h2 className="section-title">Pick a track, follow the map</h2>
          <p className="section-sub">
            Each path is a sequenced journey from fundamentals to production. Tap any module to mark it
            complete and watch your progress update — it's saved on this device.
          </p>
        </div>
        <div className="paths-grid">
          {topics.map((t) => {
            const done = progress[t.id] || [];
            const pct = Math.round((done.length / t.modules.length) * 100);
            return (
              <div className="path-card" key={t.id}>
                <div className="path-card-top">
                  <span className="path-level-pill" style={{ background: t.tint, color: t.color }}>{t.level}</span>
                  <span className="path-dur">{t.dur}</span>
                </div>
                <h3 className="path-title">
                  <span className="path-title-icon">{t.icon}</span> {t.label}
                </h3>
                <p className="path-desc">{t.desc}</p>
                <div className="path-modules">
                  {t.modules.map((m, i) => {
                    const on = done.includes(i);
                    return (
                      <button
                        key={m}
                        className={`path-module ${on ? 'on' : ''}`}
                        style={on ? { background: t.tint, borderColor: t.color, color: t.color } : undefined}
                        onClick={() => toggleModule(t.id, i)}
                      >
                        <span className="path-module-dot" style={on ? { background: t.color, borderColor: t.color } : undefined}>
                          {on ? '✓' : ''}
                        </span>
                        {m}
                      </button>
                    );
                  })}
                </div>
                <div className="path-progress-row">
                  <span className="path-progress-label">{done.length}/{t.modules.length} modules</span>
                  <span className="path-progress-pct" style={{ color: t.color }}>{pct}%</span>
                </div>
                <div className="path-progress-bar">
                  <div className="path-progress-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${t.color}, var(--cyan))` }} />
                </div>
                <Link to={t.path} className="path-explore-link">Explore {t.label} →</Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Method / 9 pillars */}
      <section className="method-section" id="how-it-works">
        <div className="section-head">
          <div className="section-eyebrow" style={{ color: 'var(--purple)' }}>// the method</div>
          <h2 className="section-title">Every topic, taught nine ways</h2>
          <p className="section-sub">We don't stop at a definition. Each concept is unpacked from nine angles so it clicks — and stays.</p>
        </div>
        <div className="pillars-grid">
          {pillars.map((p) => (
            <div className="pillar-card" key={p.n}>
              <span className="pillar-num">{p.n}</span>
              <div>
                <div className="pillar-title">{p.t}</div>
                <div className="pillar-desc">{p.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI Tutor */}
      <section className="tutor-section" id="tutor">
        <div className="tutor-grid">
          <div className="tutor-intro">
            <div className="section-eyebrow" style={{ color: 'var(--cyan)' }}>// ai tutor</div>
            <h2 className="section-title">Ask anything.<br />Get it explained properly.</h2>
            <p className="section-sub">
              Stuck on a concept or a failing pod? The tutor answers with a simple explanation, a real-world
              analogy, the YAML or CLI, and the exact commands to fix it.
            </p>
            <div className="tutor-suggestions">
              {suggestions.map((s) => (
                <button key={s} className="tutor-suggestion" onClick={() => send(s)}>
                  <span className="tutor-suggestion-q">?</span>{s}
                </button>
              ))}
            </div>
          </div>

          <div className="tutor-panel">
            <div className="tutor-panel-header">
              <span className="tutor-avatar">AI</span>
              <div>
                <div className="tutor-panel-title">braincloud Tutor</div>
                <div className="tutor-panel-status">● online · explains like you're 12, then like a pro</div>
              </div>
            </div>
            <div className="tutor-messages" ref={scrollRef}>
              {messages.map((m, i) =>
                m.role === 'user' ? (
                  <div className="tutor-msg-user" key={i}>{m.text}</div>
                ) : (
                  <div className="tutor-msg-ai" key={i}>
                    <div className="tutor-msg-topic">{m.topic}</div>
                    <div className="tutor-msg-block">
                      <span className="tutor-msg-tag" style={{ color: 'var(--cyan)' }}>SIMPLE</span>
                      {m.simple}
                    </div>
                    <div className="tutor-msg-block">
                      <span className="tutor-msg-tag" style={{ color: 'var(--purple)' }}>ANALOGY</span>
                      {m.analogy}
                    </div>
                    <div className="tutor-code">
                      <div className="tutor-code-header">
                        <span>{m.codeLang}</span>
                        <span className="tutor-code-dots"><span /><span /></span>
                      </div>
                      <pre>{m.code}</pre>
                    </div>
                    <div className="tutor-code">
                      <span className="tutor-msg-tag" style={{ color: 'var(--cyan)' }}>FIX / COMMANDS</span>
                      <pre>{m.fix}</pre>
                    </div>
                  </div>
                )
              )}
              {thinking && (
                <div className="tutor-thinking">
                  <span /><span /><span />
                </div>
              )}
            </div>
            <div className="tutor-input-row">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder="Ask about TLS, cluster upgrades, ENI vs ENA…"
                className="tutor-input"
              />
              <button className="tutor-send" onClick={() => send()}>Send</button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-brand">
          <Logo variant="full" size={32} />
          <span className="footer-brand-name">
            <span className="brand-name-light">Br</span>
            <span className="brand-name-ai">AI</span>
            <span className="brand-name-light">n</span>
            <span className="brand-name-blue">Cloud</span>
          </span>
        </div>
        <p className="footer-tagline">Learn Cloud, AI, Kubernetes &amp; OpenShift through understanding, not memorization.</p>
        <div className="footer-copy">© {new Date().getFullYear()} braincloud. Built for learners, by learners.</div>
      </footer>
    </div>
  );
}

export default Home;
